import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { useOnboardingStore, OnboardingStep } from "../../stores/onboardingStore";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuthStore } from "../../stores/authStore";
import {
  boardService,
  generateUniqueBoardName,
  Board,
} from "../../lib/api/boards";
import { subscriptionsService } from "../../lib/api/subscriptions";


import BoardListItem from "../../components/BoardListItem";
import BoardCard from "../../components/BoardCard";
import Sidebar from "../../components/Sidebar";
import { Logo } from "../../components/common/Logo";
import { PageLoader } from "../../components/LogoLoader";
import { useAlert } from "../../components/Alert";

import { useConfirmDialog } from "../../components/ConfirmDialog";
import { UserProfile } from "./components/UserProfile";
import ConnectedChannelsView from "./components/ConnectedChannelsView";
import MoneyJournalView from "./components/MoneyJournalView";
import { AddChannelModal } from "./components/modals/AddChannelModal";
import { ReturnToBoardModal } from "./components/modals/ReturnToBoardModal";
import { useUserChannelsStore } from "../../stores/userChannelsStore";
import { usePlanFeaturesStore } from "../../stores/planFeaturesStore";
import { UpgradeModal } from "../subscription/UpgradeModal";
import { ViewToggle } from "../../components/common/ViewToggle";
import { motion, AnimatePresence } from "framer-motion";



const BoardExplorerPage = () => {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { completeStep } = useOnboardingStore();
  const { } = useAuthStore();
  const alert = useAlert();
  const { showDialog } = useConfirmDialog();
  const [isCreating, setIsCreating] = useState(false);
  const [activeJournal, setActiveJournal] = useState("home");
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('grid');
  const [isAddChannelModalOpen, setIsAddChannelModalOpen] = useState(false);
  const [shouldAutoOpenJournal, setShouldAutoOpenJournal] = useState(false);
  const [searchParams] = useSearchParams();
  const [pendingReturnToBoard, setPendingReturnToBoard] = useState<{ id: string; name: string } | null>(null);
  const location = useLocation();
  const channels = useUserChannelsStore(state => state.channels);
  const initialChannelsCountRef = useRef<number | null>(null);
  // Ref to track already-processed action URLs and prevent re-processing
  // when searchParams changes as a side-effect of the navigate(cleanURL) call
  const processedActionRef = useRef(false);

  const { getFeatureValue } = usePlanFeaturesStore();
  const [upgradeModal, setUpgradeModal] = useState({ isOpen: false, featureKey: '' });


  const { data: subscriptionsResponse } = useQuery({
    queryKey: ["subscriptions"],
    queryFn: subscriptionsService.getMySubscriptions,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  const activeOrTrialingSub = subscriptionsResponse?.data?.find(s => s.status === 'active' || s.status === 'trialing');
  let currentPlan = undefined;
  if (activeOrTrialingSub) {
    if (activeOrTrialingSub.status === 'trialing') {
      currentPlan = t("profile.freeTrial", "Free trial");
    } else {
      currentPlan = activeOrTrialingSub.variant?.product?.name;
    }
  }

  // Handle tab and actions from URL
  useEffect(() => {
    const tab = searchParams.get("tab");
    const action = searchParams.get("action");

    const validTabs = ["home", "connected_channels", "money_journal"];
    const targetTab = tab && validTabs.includes(tab) ? tab : "home";

    if (activeJournal !== targetTab) {
      setActiveJournal(targetTab);
    }

    // Only process the action if it exists AND hasn't been processed yet.
    // The navigate(cleanURL) below will remove the action from the URL,
    // triggering this effect again — the ref prevents double-processing.
    if (action === "add" && !processedActionRef.current) {
      processedActionRef.current = true;

      if (tab === "connected_channels") {
        setIsAddChannelModalOpen(true);
      } else if (tab === "money_journal") {
        setShouldAutoOpenJournal(true);
      }

      // Capture return to board context if present
      const returnToBoardId = searchParams.get("returnToBoardId");
      if (returnToBoardId) {
        const returnToBoardNameStr = searchParams.get("returnToBoardName");
        const returnToBoardName = returnToBoardNameStr ? decodeURIComponent(returnToBoardNameStr) : t("boards.board");
        setPendingReturnToBoard({ id: returnToBoardId, name: returnToBoardName });
      }

      // Clear action and return params from URL after handling
      const newParams = new URLSearchParams(searchParams);
      newParams.delete("action");
      newParams.delete("returnToBoardId");
      newParams.delete("returnToBoardName");
      navigate(`/boards?${newParams.toString()}`, { replace: true });
    }

    // Reset the processed flag when there's no action in the URL
    // (so future navigations with action=add will be processed)
    if (!action) {
      processedActionRef.current = false;
    }
  }, [searchParams, navigate, activeJournal, t]);

  const handleJournalChange = (journal: string) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete("journalId"); // Clean up deep link when switching tabs
    if (journal === "home") {
      newParams.delete("tab");
    } else {
      newParams.set("tab", journal);
    }
    navigate(`?${newParams.toString()}`);
  };

  const handleSidebarJournalChange = (journal: string) => {
    if (journal.startsWith('board-')) {
      const boardId = journal.replace('board-', '');
      const board = boards.find(b => b.id === boardId);
      if (board) {
        navigate(`/boards/${boardId}`, { state: { boardName: board.name } });
      }
      return;
    }
    handleJournalChange(journal);
  };

  // Fetch boards
  const {
    data: boardsResponse,
    isLoading,
    isError,
    error,
    refetch
  } = useQuery({
    queryKey: ["boards"],
    queryFn: () => boardService.getBoards(),
    retry: (failureCount, error: any) => {
      // Retry on network errors or 5xx up to 2 times
      if (failureCount < 2 && (!error.response || error.response.status >= 500)) {
        return true;
      }
      return false;
    },
    refetchOnWindowFocus: true,
  });

  const boards = boardsResponse?.data || [];
  const fetchError = error as any;

  // Create board mutation
  const createBoardMutation = useMutation({
    mutationFn: boardService.createBoard,
    onSuccess: (response) => {
      // Invalidate and refetch boards to get fresh data from server
      queryClient.invalidateQueries({ queryKey: ["boards"] });
      // Redirect to the newly created board
      navigate(`/boards/${response.data.id}?openContent=true&editTitle=true`, { state: { boardName: response.data.name } });
    },
    onError: (error: any) => {
      console.error("Failed to create board:", error);
      alert.error(
        String(t("userErrors.boards.unableToCreate", "Unable to create your board. Please try again.")),
        String(t("errors.error"))
      );
      setIsCreating(false);
    },
    onSettled: () => {
      setIsCreating(false);
    },
  });

  // Update board mutation
  const updateBoardMutation = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      boardService.updateBoard(id, { name }),
    onSuccess: () => {
      // Invalidate and refetch boards to get fresh data from server
      queryClient.invalidateQueries({ queryKey: ["boards"] });
    },
    onError: (error: any) => {
      console.error("Failed to update board:", error);
      alert.error(
        String(t("userErrors.boards.unableToUpdate", "Unable to save your changes. Please try again.")),
        String(t("errors.error"))
      );
    },
  });

  // Delete board mutation
  const deleteBoardMutation = useMutation({
    mutationFn: boardService.deleteBoard,
    onSuccess: () => {
      // Invalidate and refetch boards to get fresh data from server
      queryClient.invalidateQueries({ queryKey: ["boards"] });
    },
    onError: (error: any) => {
      console.error("Failed to delete board:", error);
      alert.error(
        String(t("userErrors.boards.unableToDelete", "Unable to delete the board. Please try again.")),
        String(t("errors.error"))
      );
    },
  });

  const handleCreateBoard = async () => {
    if (isCreating) return;

    // Check plan limits
    const maxBoards = getFeatureValue('max_boards');
    if (typeof maxBoards === 'number' && maxBoards !== -1 && boards.length >= maxBoards) {
      setUpgradeModal({ isOpen: true, featureKey: 'max_boards' });
      return;
    }

    setIsCreating(true);
    completeStep(OnboardingStep.CREATE_BOARD);

    // Generate unique board name
    const boardName = generateUniqueBoardName(boards);

    // Create board with the generated name - backend will handle defaults
    createBoardMutation.mutate({
      name: boardName,
      description: "",
    });
  };

  const handleRenameBoard = (id: string, newName: string) => {
    updateBoardMutation.mutate({ id, name: newName });
  };

  const handleDeleteBoard = (id: string) => {
    showDialog({
      title: String(t("boards.deleteBoard")),
      message: String(t("boards.confirmDelete")),
      confirmText: String(t("actions.delete")),
      cancelText: String(t("actions.cancel")),
      onConfirm: () => {
        deleteBoardMutation.mutate(id);
      },
    });
  };

  /* State for Return To Board Modal */
  const [isReturnToBoardModalOpen, setIsReturnToBoardModalOpen] = useState(false);
  const [returnToBoardData, setReturnToBoardData] = useState<{ id: string; name: string; channelId?: string } | null>(null);

  const handleReturnToBoardFlow = (channelId?: string) => {
    if (pendingReturnToBoard) {
      setReturnToBoardData({ ...pendingReturnToBoard, channelId });
      setIsReturnToBoardModalOpen(true);
      setPendingReturnToBoard(null); // Clear pending state after use
    }
  };

  const handleConfirmReturnToBoard = () => {
    if (returnToBoardData) {
      let query = "?openContent=true";

      // Determine which modal to open based on what the user was doing
      if (activeJournal === "connected_channels") {
        query = "?openModal=select_channel_source";

        // Pass the new channel info so BoardPage can show sync indicator.
        // BoardPage decides itself (from channelTypeConfig.pseudoChannelEnabled)
        // whether to open the pseudo-channel picker — no flag needed.
        if (returnToBoardData.channelId) {
          query += `&newChannelId=${returnToBoardData.channelId}`;
        }
      } else if (activeJournal === "money_journal") {
        query = "?openModal=select_journal_source";
      }

      navigate(`/boards/${returnToBoardData.id}${query}`, { state: { boardName: returnToBoardData.name } });
      setIsReturnToBoardModalOpen(false);
      setReturnToBoardData(null);
    }
  };

  const handleAddChannelComplete = (channelId?: string) => {
    setIsAddChannelModalOpen(false);
    // Refresh channels store
    useUserChannelsStore.getState().loadChannels(true);

    // Invalidate board queries to reflect new channel availability
    queryClient.invalidateQueries({ queryKey: ['board-payload'] });

    completeStep(OnboardingStep.ADD_DATA);
    handleReturnToBoardFlow(channelId);
  };

  // Re-check channels when tab is focused or becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // If we're on the channels tab, refresh all channels
        if (activeJournal === 'connected_channels') {
          useUserChannelsStore.getState().loadChannels(true);
        } else if (pendingReturnToBoard) {
          // Force refresh channel count to see if we connected something in another tab
          useUserChannelsStore.getState().loadChannels(true);
        }
      }
    };

    window.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleVisibilityChange);
    return () => {
      window.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleVisibilityChange);
    };
  }, [pendingReturnToBoard, activeJournal]);

  // Capture initial channel count when starting "add channel" flow
  useEffect(() => {
    if (pendingReturnToBoard && initialChannelsCountRef.current === null) {
      initialChannelsCountRef.current = channels.length;
    } else if (!pendingReturnToBoard) {
      initialChannelsCountRef.current = null;
    }
  }, [pendingReturnToBoard, channels.length]);

  // Monitor for connection success (either via state or channel count increase)
  useEffect(() => {
    if (pendingReturnToBoard) {
      const hasNewChannel = initialChannelsCountRef.current !== null &&
        channels.length > initialChannelsCountRef.current;
      const stateSuccess = location.state?.channelConnected;

      if (hasNewChannel || stateSuccess) {
        const channelId = location.state?.channelId;
        handleReturnToBoardFlow(channelId);

        // If it was state-based, clear the state to prevent double-trigger
        if (stateSuccess) {
          navigate(location.pathname + location.search, { replace: true, state: {} });
        }
      }
    }
  }, [channels.length, location.state, pendingReturnToBoard, navigate, location.pathname, location.search]);

  // Error handling is now more resilient - we don't show a full page error
  // unless it's a critical application failure. Data errors are logged.
  useEffect(() => {
    if (error) {
      console.error('BoardExplorer data fetch error:', error);
      // We could add a toast/alert here if needed
    }
  }, [error]);

  if (isLoading) {
    return <PageLoader />;
  }


  return (
    <div className="h-[calc(100vh_-_var(--billing-banner-h,0px))] w-full flex bg-background-page dark:bg-neutral-950 overflow-hidden">

      <div className="hidden lg:flex lg:flex-col sidebar-container">
        <div className="h-18 flex items-center px-6 shrink-0 border-b border-neutral-100/50 dark:border-neutral-800/30">
          <div
            onClick={() => handleJournalChange('home')}
            className="group flex items-center gap-3 px-3 py-2 -ml-3 rounded-xl cursor-pointer hover:bg-neutral-50 dark:hover:bg-neutral-800/40 transition-all duration-300 active:scale-[0.98]"
          >
            <div className="relative">
              <div className="absolute inset-0 bg-accent-primary/20 blur-lg rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <Logo className="w-7 h-7 relative z-10 transition-transform duration-300 group-hover:scale-110" />
            </div>
            <span className="text-xl font-medium font-display text-text-primary dark:text-neutral-100 tracking-tight group-hover:text-accent-primary transition-colors">
              Dashbal
            </span>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          <Sidebar
            activeJournal={activeJournal}
            onJournalChange={handleSidebarJournalChange}
            boards={boards}
            onCreateBoard={handleCreateBoard}
          />
        </div>
        <UserProfile planName={currentPlan} />
      </div>

      {/* Mobile Drawer */}
      <div
        className={`fixed inset-y-0 left-0 z-50 transform transition-transform duration-300 ease-in-out lg:hidden sidebar-mobile flex flex-col ${isSidebarOpen ? "translate-x-0" : "-translate-x-full"
          }`}
      >
        <div className="h-18 flex items-center px-6 shrink-0 border-b border-neutral-100/50 dark:border-neutral-800/30">
          <div
            onClick={() => handleJournalChange('home')}
            className="group flex items-center gap-3 px-3 py-2 -ml-3 rounded-xl cursor-pointer hover:bg-neutral-50 dark:hover:bg-neutral-800/40 transition-all duration-300 active:scale-[0.98]"
          >
            <div className="relative">
              <div className="absolute inset-0 bg-accent-primary/20 blur-lg rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <Logo className="w-7 h-7 relative z-10 transition-transform duration-300 group-hover:scale-110" />
            </div>
            <span className="text-xl font-medium font-display text-text-primary dark:text-neutral-100 tracking-tight group-hover:text-accent-primary transition-colors">
              Dashbal
            </span>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          <Sidebar
            activeJournal={activeJournal}
            onJournalChange={(journal) => {
              handleSidebarJournalChange(journal);
              setIsSidebarOpen(false);
            }}
            boards={boards}
            onCreateBoard={handleCreateBoard}
          />
        </div>
        <UserProfile planName={currentPlan} />
      </div>

      {/* Click Outside to Close - Invisible Overlay */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 z-40 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-w-0">


        {/* Main Content */}
        {/* Main Content */}
        <main className="flex-1 flex flex-col min-h-0 overflow-y-auto">
          <div className="flex-1 flex flex-col px-4 pb-4 pt-0 lg:p-4 min-h-full">
            <AnimatePresence mode="wait">
            {activeJournal === "home" && (
              <motion.div 
                key="home"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="flex-1 flex flex-col min-h-0"
              >
                {/* Only show create button in header when there are boards */}
                {boards.length > 0 && (
                  <div className="flex items-center justify-between mb-4 pt-4 lg:pt-0 lg:mb-6 gap-3">
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => setIsSidebarOpen(true)}
                        className="lg:hidden p-2 -ml-2 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-800 text-neutral-500 dark:text-neutral-400"
                      >
                        <span className="w-6 h-6" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_df5a8182.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_df5a8182.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                      </button>
                      <div className="flex flex-col">
                        <h2 className="text-xl font-bold text-text-primary dark:text-neutral-100 tracking-tight">
                          {t("boards.title", "Boards")}
                        </h2>
                        <p className="text-xs text-neutral-500 dark:text-neutral-400 hidden lg:block">
                          {t("boards.subtitle", "Manage your interactive financial boards")}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="hidden lg:block">
                        <ViewToggle view={viewMode} onViewChange={setViewMode} />
                      </div>
                      <div className="relative">
                        <button
                          id="onboarding-create-board"
                          onClick={handleCreateBoard}
                          disabled={isCreating}
                          className={`h-8 px-4 text-xs font-bold bg-accent-primary text-white dark:text-white rounded-lg hover:bg-accent-primary/90 transition-all shadow-sm active:scale-95 flex items-center gap-2`}

                        >
                          <span className="w-3.5 h-3.5" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_boards_lamp.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_boards_lamp.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                          {t(isCreating ? "actions.creating" : "boards.createBoard")}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Boards List or Error State */}
                {isError ? (
                  <div className="flex flex-col items-center justify-center h-full max-w-md mx-auto text-center px-4">
                    <div className="w-16 h-16 bg-red-50 dark:bg-red-900/20 rounded-full flex items-center justify-center mb-6">
                      <span className="w-8 h-8 text-red-500" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_067ca6e1.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_067ca6e1.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                    </div>
                    <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-2">
                      {t('errors.failedToLoadBoards', 'Failed to load boards')}
                    </h3>
                    <p className="text-neutral-500 dark:text-neutral-400 mb-8">
                      {fetchError?.message?.includes('Network') || !fetchError?.response
                        ? t('errors.connectionIssue', 'There was a problem connecting to the server. Please check your internet connection.')
                        : t('errors.somethingWentWrong', 'Something went wrong while trying to fetch your boards.')}
                    </p>
                    <button
                      onClick={() => refetch()}
                      className="h-9 px-6 text-xs font-bold bg-neutral-900 dark:bg-neutral-100 text-white dark:text-neutral-900 rounded-lg hover:opacity-90 transition-all active:scale-95 shadow-sm"
                    >
                      {t('actions.tryAgain', 'Try Again')}
                    </button>
                  </div>
                ) : boards.length > 0 ? (
                  <>
                    {/* Board items */}
                    {viewMode === 'list' ? (
                      <div className="bg-white dark:bg-neutral-900 rounded-xl border border-neutral-200 dark:border-neutral-800 overflow-hidden shadow-sm">


                        {/* Table headers */}
                        <div className="hidden md:flex items-center py-2 px-4 border-b border-neutral-100 dark:border-neutral-800 bg-neutral-50/50 dark:bg-neutral-800/50">
                          <div className="flex-1 min-w-0">
                            <span className="text-[10px] font-semibold text-neutral-400 dark:text-neutral-500 uppercase tracking-wider">
                              {t("boards.name")}
                            </span>
                          </div>
                          <div className="flex-shrink-0 w-32 px-3">
                            <span className="text-[10px] font-semibold text-neutral-400 dark:text-neutral-500 uppercase tracking-wider">
                              {t("boards.lastModified")}
                            </span>
                          </div>
                          <div className="hidden lg:block flex-shrink-0 w-32 px-3">
                            <span className="text-[10px] font-semibold text-neutral-400 dark:text-neutral-500 uppercase tracking-wider">
                              {t("boards.owner")}
                            </span>
                          </div>
                          <div className="flex-shrink-0 w-10"></div>
                        </div>
                        {boards.map((board: Board) => (
                          <BoardListItem
                            key={board.id}
                            board={board}
                            onRename={handleRenameBoard}
                            onDelete={handleDeleteBoard}
                          />
                        ))}
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                        {boards.map((board: Board, index: number) => (
                          <BoardCard
                            key={board.id}
                            board={board}
                            onRename={handleRenameBoard}
                            onDelete={handleDeleteBoard}
                            index={index}
                          />
                        ))}
                      </div>
                    )}

                    {/* Short list tips */}
                    {boards.length < 5 && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3 }}
                        className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6"
                      >
                        <div 
                          onClick={() => handleJournalChange('connected_channels')}
                          className="p-5 bg-white dark:bg-neutral-900 rounded-xl border border-neutral-200 dark:border-neutral-800 shadow-sm hover:shadow-md hover:border-accent-primary/30 transition-all group cursor-pointer"
                        >
                          <div className="w-10 h-10 bg-purple-50 dark:bg-purple-900/20 text-purple-500 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-inner">
                            <span className="w-5 h-5" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_channels.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_channels.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                          </div>
                          <h4 className="text-sm font-semibold text-text-primary dark:text-neutral-200 mb-1.5">{t("boards.tips.connectSources")}</h4>
                          <p className="text-xs text-neutral-500 dark:text-neutral-400 leading-relaxed font-light">
                            {t("boards.tips.connectSourcesDesc")}
                          </p>
                        </div>
                        <div 
                          onClick={() => handleJournalChange('money_journal')}
                          className="p-5 bg-white dark:bg-neutral-900 rounded-xl border border-neutral-200 dark:border-neutral-800 shadow-sm hover:shadow-md hover:border-accent-primary/30 transition-all group cursor-pointer"
                        >
                          <div className="w-10 h-10 bg-green-50 dark:bg-green-900/20 text-green-500 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-inner">
                            <span className="w-5 h-5" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_journal.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_journal.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                          </div>
                          <h4 className="text-sm font-semibold text-text-primary dark:text-neutral-200 mb-1.5">{t("boards.tips.moneyJournal")}</h4>
                          <p className="text-xs text-neutral-500 dark:text-neutral-400 leading-relaxed font-light">
                            {t("boards.tips.moneyJournalDesc")}
                          </p>
                        </div>
                        <div 
                          onClick={() => navigate('/docs')}
                          className="p-5 bg-white dark:bg-neutral-900 rounded-xl border border-neutral-200 dark:border-neutral-800 shadow-sm hover:shadow-md hover:border-accent-primary/30 transition-all group cursor-pointer"
                        >
                          <div className="w-10 h-10 bg-blue-50 dark:bg-blue-900/20 text-blue-500 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-inner">
                            <span className="w-5 h-5" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_92d8a988.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_92d8a988.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                          </div>
                          <h4 className="text-sm font-semibold text-text-primary dark:text-neutral-200 mb-1.5">{t("boards.tips.support")}</h4>
                          <p className="text-xs text-neutral-500 dark:text-neutral-400 leading-relaxed font-light">
                            {t("boards.tips.supportDesc")}
                          </p>
                        </div>

                      </motion.div>
                    )}
                  </>
                ) : (
                  <motion.div 
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                    className="flex-1 flex flex-col"
                  >
                    {/* Unified Header Placeholder */}
                    <div className="flex items-center justify-between mb-4 pt-4 lg:pt-0 lg:mb-6 shrink-0">
                      <button
                        onClick={() => setIsSidebarOpen(true)}
                        className="lg:hidden p-2 -ml-2 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-800 text-neutral-500 dark:text-neutral-400"
                      >
                        <span className="w-6 h-6" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_df5a8182.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_df5a8182.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                      </button>
                    </div>
                    
                    <div className="flex-1 flex flex-col items-center justify-center">
                      <div className="text-center">
                      <div className="w-12 h-12 bg-neutral-100 dark:bg-neutral-800 rounded-xl flex items-center justify-center mx-auto mb-3">

                        <span className="w-6 h-6 text-neutral-400" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_f99624b4.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_f99624b4.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                      </div>
                      <h3 className="text-sm font-medium text-text-primary dark:text-neutral-200 mb-1">
                        {t("boards.noBoards")}
                      </h3>
                      <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-4 font-light">
                        {t("boards.createFirstBoard")}
                      </p>
                      <div className="relative inline-block">
                        <button
                          id="onboarding-create-board"
                          onClick={handleCreateBoard}
                          disabled={isCreating}
                          className={`h-7 px-3 text-xs font-bold bg-accent-primary text-white dark:text-white rounded-lg hover:bg-accent-primary/90 transition-all active:scale-95 shadow-sm`}

                        >
                          {t(isCreating ? "actions.creating" : "boards.createBoard")}
                        </button>
                      </div>
                    </div>

                    {/* Integrated Tips for Onboarding */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 w-full max-w-4xl">
                      <div 
                        onClick={() => handleJournalChange('connected_channels')}
                        className="p-5 bg-white dark:bg-neutral-900 rounded-xl border border-neutral-200 dark:border-neutral-800 shadow-sm hover:shadow-md hover:border-accent-primary/30 transition-all group cursor-pointer"
                      >
                        <div className="w-10 h-10 bg-purple-50 dark:bg-purple-900/20 text-purple-500 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-inner">
                          <span className="w-5 h-5" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_channels.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_channels.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                        </div>
                        <h4 className="text-sm font-semibold text-text-primary dark:text-neutral-200 mb-1.5">{t("boards.tips.connectSources")}</h4>
                        <p className="text-xs text-neutral-500 dark:text-neutral-400 leading-relaxed font-light">
                          {t("boards.tips.connectSourcesDesc")}
                        </p>
                      </div>
                      <div 
                        onClick={() => handleJournalChange('money_journal')}
                        className="p-5 bg-white dark:bg-neutral-900 rounded-xl border border-neutral-200 dark:border-neutral-800 shadow-sm hover:shadow-md hover:border-accent-primary/30 transition-all group cursor-pointer"
                      >
                        <div className="w-10 h-10 bg-green-50 dark:bg-green-900/20 text-green-500 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-inner">
                          <span className="w-5 h-5" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_journal.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_journal.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                        </div>
                        <h4 className="text-sm font-semibold text-text-primary dark:text-neutral-200 mb-1.5">{t("boards.tips.moneyJournal")}</h4>
                        <p className="text-xs text-neutral-500 dark:text-neutral-400 leading-relaxed font-light">
                          {t("boards.tips.moneyJournalDesc")}
                        </p>
                      </div>
                      <div 
                        onClick={() => window.open('https://docs.dashbal.com', '_blank')}
                        className="p-5 bg-white dark:bg-neutral-900 rounded-xl border border-neutral-200 dark:border-neutral-800 shadow-sm hover:shadow-md hover:border-accent-primary/30 transition-all group cursor-pointer"
                      >
                        <div className="w-10 h-10 bg-blue-50 dark:bg-blue-900/20 text-blue-500 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-inner">
                          <span className="w-5 h-5" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_92d8a988.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_92d8a988.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                        </div>
                        <h4 className="text-sm font-semibold text-text-primary dark:text-neutral-200 mb-1.5">{t("boards.tips.support")}</h4>
                        <p className="text-xs text-neutral-500 dark:text-neutral-400 leading-relaxed font-light">
                          {t("boards.tips.supportDesc")}
                        </p>
                    </div>
                  </div>
                </div>
                </motion.div>
              )}
            </motion.div>
          )}

            {
              activeJournal === "connected_channels" && (
                <motion.div
                  key="channels"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="flex-1 flex flex-col min-h-0"
                >
                  <ConnectedChannelsView 
                    onAddChannel={() => setIsAddChannelModalOpen(true)} 
                    onMenuClick={() => setIsSidebarOpen(true)}
                  />
                </motion.div>
              )
            }
            {
              activeJournal === "money_journal" && (
                <motion.div
                  key="journals"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="flex-1 flex flex-col min-h-0"
                >
                  <MoneyJournalView
                    autoOpenAdd={shouldAutoOpenJournal}
                    onAddConsumed={() => setShouldAutoOpenJournal(false)}
                    onAddComplete={() => completeStep(OnboardingStep.ADD_DATA)}
                    returnToBoard={pendingReturnToBoard}
                    onMenuClick={() => setIsSidebarOpen(true)}
                    onReturnToBoard={() => {
                      if (pendingReturnToBoard) {
                        const query = "?openModal=select_journal_source";
                        navigate(`/boards/${pendingReturnToBoard.id}${query}`, { state: { boardName: pendingReturnToBoard.name } });
                        setPendingReturnToBoard(null);
                      }
                    }}
                  />
                </motion.div>
              )
            }
            </AnimatePresence>
          </div >
        </main >
      </div >
      <AddChannelModal
        isOpen={isAddChannelModalOpen}
        onClose={() => {
          setIsAddChannelModalOpen(false);
        }}
        onComplete={handleAddChannelComplete}
      />
      <ReturnToBoardModal
        isOpen={isReturnToBoardModalOpen}
        onClose={() => setIsReturnToBoardModalOpen(false)}
        onConfirm={handleConfirmReturnToBoard}
        boardName={returnToBoardData?.name || ''}
      />
      <UpgradeModal
        isOpen={upgradeModal.isOpen}
        onClose={() => setUpgradeModal({ isOpen: false, featureKey: '' })}
        featureKey={upgradeModal.featureKey}
        currentUsage={boards.length}
      />
    </div >
  );
};

export default BoardExplorerPage;
